import { skipToken } from "@reduxjs/toolkit/query/react";
import type React from "react";
import { Route, Routes } from "react-router";
import { AccountsPage } from "./components/accounts/AccountsPage";
import { AnalyticsHomePage } from "./components/AnalyticsHomePage";
import { AppearancePage } from "./components/appearance/AppearancePage";
import { AppLayout } from "./components/AppLayout";
import { AppRedirect } from "./components/AppRedirect";
import { AcceptInvitePage } from "./components/authentication/AcceptInvitePage";
import { AuthLayout } from "./components/authentication/AuthLayout";
import { ForgotPasswordPage } from "./components/authentication/ForgotPasswordPage";
import { LoginPage } from "./components/authentication/LoginPage";
import { NoOrgPage } from "./components/authentication/NoOrgPage";
import { ResetPasswordPage } from "./components/authentication/ResetPasswordPage";
import { ValidateEmailPage } from "./components/authentication/ValidateEmailPage";
import { DevelopmentPage } from "./components/development/DevelopmentPage";
import { LoginRedirect } from "./components/LoginRedirect";
import { NoAppsPage } from "./components/NoAppsPage";
import { PlanEditPage } from "./components/PlanEditPage/PlanEditPage";
import { PlansPage } from "./components/PlansPage";
import { PlaybookFAQPage } from "./components/playbook/PlaybookFAQPage";
import { PlaybookGuidePage } from "./components/playbook/PlaybookGuidePage";
import { PlaybookLayout } from "./components/playbook/PlaybookLayout";
import { PlaybookPage } from "./components/playbook/PlaybookPage";
import { PlaybookSectionPage } from "./components/playbook/PlaybookSectionPage";
import { RequireRole } from "./components/RequireRole";
import { PersonalSettingsPage } from "./components/settings/PersonalSettingsPage";
import { SettingsLayout } from "./components/settings/SettingsLayout";
import { TeamSettingsPage } from "./components/settings/TeamSettingsPage";
import { StaffSessionLayout } from "./components/StaffSessionLayout";
import { api } from "./state/api";
import { Role, type AppResponse } from "./state/api.types";
import { useTypedSelector } from "./state/store";

export const Router = () => {
  const { orgSlug } = useTypedSelector((state) => state.local);
  const getUser = api.useGetUserQuery(null);
  const getApps = api.useGetAppsQuery(
    getUser.currentData && orgSlug ? orgSlug : skipToken,
  );

  if (getUser.isLoading || getApps.isLoading) {
    return null;
  }

  const user = getUser.currentData;

  if (!user) {
    return (
      <Routes>
        <Route element={<AuthLayout />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
          <Route path="accept-invite" element={<AcceptInvitePage />} />
        </Route>
        <Route path="*" element={<LoginRedirect />} />
      </Routes>
    );
  }

  if (!user.organizations.length) {
    return (
      <Routes>
        <Route element={<AuthLayout />}>
          <Route path="accept-invite" element={<AcceptInvitePage />} />
          <Route path="*" element={<NoOrgPage />} />
        </Route>
      </Routes>
    );
  }

  return <AuthedRouter apps={getApps.currentData?.data ?? []} />;
};

type AuthedRouterProps = {
  apps: AppResponse[];
};

const AuthedRouter = ({ apps }: AuthedRouterProps) => {
  const appSlug = useTypedSelector((state) => state.local.appSlug);

  const app = apps.find((a) => a.slug === appSlug);

  const appRoutes: {
    path: string;
    element: (app: AppResponse) => React.ReactNode;
  }[] = app
    ? [
        {
          path: "accounts",
          element: (app) => <AccountsPage app={app} key={app.uuid} />,
        },
        {
          path: "accounts/:accountId",
          element: (app) => <AccountsPage app={app} key={app.uuid} />,
        },
        {
          path: "plans",
          element: (app) => <PlansPage app={app} key={app.uuid} />,
        },
        {
          path: "plans/programs/:programId",
          element: (app) => <PlansPage app={app} key={app.uuid} />,
        },
        {
          path: "plans/:planId",
          element: (app) => <PlansPage app={app} key={app.uuid} />,
        },
        {
          path: "plans/:planId/edit",
          element: (app) => (
            <RequireRole roles={[Role.Developer, Role.OrgAdmin]}>
              <PlanEditPage app={app} key={app.uuid} />
            </RequireRole>
          ),
        },
        {
          path: "development",
          element: (app) => (
            <RequireRole roles={[Role.Developer, Role.OrgAdmin]}>
              <DevelopmentPage app={app} key={app.uuid} />
            </RequireRole>
          ),
        },
        {
          path: "development/webhooks/:webhookId",
          element: (app) => (
            <RequireRole roles={[Role.Developer, Role.OrgAdmin]}>
              <DevelopmentPage app={app} key={app.uuid} />
            </RequireRole>
          ),
        },
        {
          path: "appearance",
          element: (app) => (
            <RequireRole roles={[Role.Developer, Role.OrgAdmin]}>
              <AppearancePage app={app} key={app.uuid} />
            </RequireRole>
          ),
        },
      ]
    : [];

  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route path="accept-invite" element={<AcceptInvitePage />} />
        <Route
          path="my-profile/validate-email"
          element={<ValidateEmailPage />}
        />
      </Route>
      <Route element={<StaffSessionLayout />}>
        <Route element={<AppLayout app={app} apps={apps} />}>
          <Route
            path="apps"
            element={apps.length ? <AppRedirect /> : <NoAppsPage />}
          />
          {app ? (
            <Route path="apps/:appId">
              <Route
                index
                element={
                  <RequireRole roles={[Role.Developer, Role.OrgAdmin]}>
                    <AnalyticsHomePage app={app} key={app.uuid} />
                  </RequireRole>
                }
              />
              {appRoutes.map((props) => (
                <Route
                  key={props.path}
                  path={props.path}
                  element={props.element(app)}
                />
              ))}
            </Route>
          ) : (
            <Route path="apps/:appId/*" element={<AppRedirect />} />
          )}
          {appRoutes.map((props) => (
            // Redirect routes that don't require `/apps/:appId` prefix
            <Route
              key={props.path}
              path={props.path}
              element={<AppRedirect appPath={props.path} />}
            />
          ))}
          <Route path="settings" element={<SettingsLayout />}>
            <Route path="personal" element={<PersonalSettingsPage />} />
            <Route path="team" element={<TeamSettingsPage />} />
          </Route>
          <Route path="playbook" element={<PlaybookLayout />}>
            <Route index element={<PlaybookPage />} />
            <Route path="section/:slug" element={<PlaybookSectionPage />} />
            <Route
              path="guides/:shortId/:slug?"
              element={<PlaybookGuidePage />}
            />
            <Route path="faqs/:shortId/:slug?" element={<PlaybookFAQPage />} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<AppRedirect />} />
    </Routes>
  );
};
